import { z } from "zod";

/**
 * `iamu/quality-gate.yaml` — the project's quality-gate configuration
 * (spec `quality-gate`, "Gate config lives in the repo"). Synced to the
 * cloud as entity kind `gate_config`; when a project has none, the
 * built-in `iamu:recommended` preset applies.
 *
 * ```yaml
 * version: 1
 * preset: iamu:recommended
 * baseline:
 *   mode: previous_run          # previous_run | previous_build | reference_branch | pinned_build | date | days
 *   environment: same           # same | any
 * conditions:
 *   G6: { level: warn, threshold: 0.5 }
 *   G9: { level: warn, threshold: 3 }
 * capabilities:
 *   checkout:
 *     conditions:
 *       G9: { level: error, threshold: 0 }
 * scopes:
 *   search: { capabilities: [search, filters] }
 * ```
 *
 * Unknown keys are rejected so a typo (`G10`, `levle`) fails the sync
 * with a path instead of silently doing nothing.
 */

export const GATE_CONDITION_IDS = [
  "G1",
  "G2",
  "G3",
  "G4",
  "G5",
  "G6",
  "G7",
  "G8",
  "G9",
] as const;
export type GateConditionId = (typeof GATE_CONDITION_IDS)[number];

export const GATE_LEVELS = ["error", "warn", "off"] as const;
export type GateLevel = (typeof GATE_LEVELS)[number];

export const BASELINE_MODES = [
  "previous_run",
  "previous_build",
  "reference_branch",
  "pinned_build",
  "date",
  "days",
] as const;
export type BaselineMode = (typeof BASELINE_MODES)[number];

export const GATE_PRESET = "iamu:recommended";

const conditionIdSchema = z.enum(GATE_CONDITION_IDS);

const conditionSettingSchema = z
  .object({
    level: z.enum(GATE_LEVELS).optional(),
    /** Count limit (G1–G5, G7–G9) or minimum ratio 0–1 (G6). */
    threshold: z.number().min(0).optional(),
  })
  .strict();

const conditionMapSchema = z.partialRecord(
  conditionIdSchema,
  conditionSettingSchema,
);

const keySchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-z0-9]+(-+[a-z0-9]+)*$|^[0-9a-f-]{36}$/i, {
    message: "must be a capability slug or id",
  });

export const gateBaselineConfigSchema = z
  .object({
    /** Omit to pick by build: previous_run for implicit builds, else previous_build. */
    mode: z.enum(BASELINE_MODES).optional(),
    /** `same` (default): only runs against the candidate's environment count. */
    environment: z.enum(["same", "any"]).default("same"),
    /** reference_branch mode: the branch whose latest build is the baseline. */
    branch: z.string().min(1).max(255).default("main"),
    /** pinned_build mode: a build label or id. */
    build: z.string().min(1).max(200).optional(),
    /** date mode: the state as of this instant. */
    date: z.string().datetime({ offset: true }).optional(),
    /** days mode: the state as of N days before the candidate. */
    days: z.number().int().min(1).max(3650).optional(),
  })
  .strict();

export const gateConfigSchema = z
  .object({
    version: z.literal(1).default(1),
    preset: z.literal(GATE_PRESET).default(GATE_PRESET),
    baseline: gateBaselineConfigSchema.default({
      environment: "same",
      branch: "main",
    }),
    conditions: conditionMapSchema.default({}),
    /** Per-capability overrides, keyed by capability slug (or id). */
    capabilities: z
      .record(
        keySchema,
        z.object({ conditions: conditionMapSchema.default({}) }).strict(),
      )
      .default({}),
    /** Named scopes usable as `scope` on an evaluation. */
    scopes: z
      .record(
        keySchema,
        z.object({ capabilities: z.array(keySchema).min(1) }).strict(),
      )
      .default({}),
  })
  .strict();

export type GateConfigInput = z.input<typeof gateConfigSchema>;
export type GateConfig = z.infer<typeof gateConfigSchema>;

export interface ConditionSetting {
  level: GateLevel;
  threshold: number;
}

/** The `iamu:recommended` preset (design D9). */
export const RECOMMENDED_CONDITIONS: Readonly<
  Record<GateConditionId, ConditionSetting>
> = {
  G1: { level: "error", threshold: 0 },
  G2: { level: "error", threshold: 0 },
  G3: { level: "error", threshold: 0 },
  G4: { level: "error", threshold: 0 },
  G5: { level: "warn", threshold: 0 },
  G6: { level: "warn", threshold: 0.6 },
  G7: { level: "warn", threshold: 0 },
  G8: { level: "warn", threshold: 0 },
  G9: { level: "off", threshold: 0 },
};

/** The config used when a project has no `quality-gate.yaml`. */
export const DEFAULT_GATE_CONFIG: GateConfig = gateConfigSchema.parse({});

/** Preset conditions with the config's overrides applied. */
export function resolveConditions(
  overrides: GateConfig["conditions"] | undefined,
): Record<GateConditionId, ConditionSetting> {
  const out = {} as Record<GateConditionId, ConditionSetting>;
  for (const id of GATE_CONDITION_IDS) {
    out[id] = { ...RECOMMENDED_CONDITIONS[id], ...(overrides?.[id] ?? {}) };
  }
  return out;
}

/** Parse + validate a config; the error path names the offending field. */
export function parseGateConfig(
  input: unknown,
):
  | { success: true; config: GateConfig }
  | { success: false; path: string; message: string } {
  const parsed = gateConfigSchema.safeParse(input ?? {});
  if (parsed.success) return { success: true, config: parsed.data };
  const issue = parsed.error.issues[0];
  const keys =
    issue && "keys" in issue && Array.isArray(issue.keys) ? issue.keys : [];
  const path = [...(issue?.path ?? []), ...keys].map(String).join(".");
  return {
    success: false,
    path,
    message: issue?.message ?? "invalid quality-gate config",
  };
}
